import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Container, Group, Image, Paper, Stack, Text, TextInput, Title } from '@mantine/core'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useRequestPasswordRecoveryMutation } from '../../api/auth/api'
import { RouterButton } from '../../components/RouterButton'

const schema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
})

type FormData = z.infer<typeof schema>

export const Route = createFileRoute('/recovery/forgot')({
  component: ForgotPasswordScreen,
  validateSearch: z.object({
    email: z.string().optional(),
  }),
})

function ForgotPasswordScreen() {
  const { mutate: requestRecovery, isPending } = useRequestPasswordRecoveryMutation()
  const navigate = useNavigate()
  const { email } = useSearch({ from: '/recovery/forgot' })

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: email ?? '',
    },
  })

  const onSubmit = handleSubmit((data) => {
    requestRecovery(data, {
      onSuccess: () => {
        navigate({
          to: '/recovery/otp-v2',
          search: { email: data.email },
        })
      },
    })
  })

  return (
    <Stack
      align="center"
      justify="center"
      style={{
        height: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #ff8800 0%, #ff6600 35%, #ff4400 100%)',
      }}
    >
      <Container size={600}>
        <Paper withBorder shadow="md" p={30} mt={30} radius="md" w={500}>
          <Image h={40} w="auto" fit="contain" src="/carma_partner_logo.svg" alt="Carma Logo" />
          <Title order={3} style={{ fontWeight: 700, color: '#333' }} mt="md">
            Reset your password
          </Title>
          <Text size="sm" mb={30}>
            We'll send you an email with a code to reset your password.
          </Text>
          <form onSubmit={onSubmit}>
            <TextInput
              label="Email"
              placeholder="you@example.com"
              required
              {...register('email')}
              error={errors.email?.message}
            />
            <Group gap={8} justify="center" mt="xl">
              <RouterButton w={'49%'} variant="outline" to="/login">
                Return to login
              </RouterButton>
              <Button w={'49%'} type="submit" loading={isPending}>
                Send recovery code
              </Button>
            </Group>
          </form>
        </Paper>
      </Container>
    </Stack>
  )
}
